/**
 * Running Cost Estimator
 *
 * Calculates the operational cost per sauna session and monthly estimate
 * based on heater power, sauna volume, insulation, and energy prices.
 *
 * Physics model:
 *   Energy to heat air     = volume × ΔT × ρ_air × c_air  (small — ~0.15 kWh)
 *   Energy to heat stones  = ~heater_kW × 1500 kJ (stone mass scales with
 *                            heater size; ~40 kg at 6 kW, ΔT ≈ 270 K)
 *   Energy heater body     = ~heater_kW × 300 kJ
 *   Energy panel/benches   = ~volume × 550 kJ/m³ (the lining layer that has
 *                            warmed through by the time the room is ready)
 *   Heat-up time = total energy / (heater_kW × efficiency)
 *   Session cost = kWh consumed × price/kWh
 *
 * Calibration anchor: a 6 kW heater in a ~6 m³ sauna heats up in ~45-50 min
 * and a full session lands around 6-7 kWh — matching manufacturer guidance.
 * (The air term alone is tiny: modelling only air × a small factor is what
 * previously produced an absurd "5 min heat-up, 3.5 kr/session".)
 *
 * For wood-fired: estimates ~15 kg birch per session for a typical sauna.
 */

import type { HeaterType } from '../../engines/types';
import type { Market } from '../location/types';

// ─── Types ──────────────────────────────────────────────────

export interface RunningCostInput {
  /** Heater power in kW (from calculateHeaterRequirements) */
  heaterKw: number;
  /** Internal sauna volume in m³ (W × L × ceiling height) */
  volumeM3: number;
  /** Heater type — wood or electric */
  heaterType: HeaterType;
  /** Glass area in m² (increases heat loss) */
  glassAreaSqm?: number;
  /** Whether the sauna is an uninsulated outdoor log cabin */
  isOutdoorLogCabin?: boolean;
  /** Override electricity price, SEK per kWh (default: 1.50) */
  electricityPriceSekPerKwh?: number;
  /** Sessions per month estimate (default: 8, i.e. 2/week) */
  sessionsPerMonth?: number;
  /**
   * Market the estimate is for. Selects the market-dependent default
   * firewood price; every returned amount stays in SEK and is converted at
   * the render boundary by `formatPrice`. Defaults to 'se'.
   */
  market?: Market;
  /**
   * Explicit override for the wood fuel cost per session, in the caller's
   * display currency. Takes precedence over the market default below — kept
   * for callers (e.g. the standalone running-cost calculator) that already
   * compute their own market-converted figure.
   */
  woodCostPerSession?: number;
}

export interface RunningCostResult {
  /** Estimated minutes to reach target temperature (80 °C) */
  heatUpMinutes: number;
  /** Estimated energy per session in kWh */
  kwhPerSession: number;
  /** Estimated cost per session in SEK */
  sessionCostSek: number;
  /** Estimated monthly cost in SEK */
  costPerMonthSek: number;
}

// ─── Constants ──────────────────────────────────────────────

/** Target temperature in °C */
const TARGET_TEMP_C = 80;
/** Ambient starting temperature in °C */
const AMBIENT_TEMP_C = 10;
/** Temperature delta */
const DELTA_T_C = TARGET_TEMP_C - AMBIENT_TEMP_C;

/** Air density at ~20 °C in kg/m³ */
const AIR_DENSITY = 1.2;
/** Specific heat of air in kJ/(kg·K) */
const AIR_SPECIFIC_HEAT = 1.005;

/**
 * Stone + heater-body energy per kW of heater power, in kJ.
 * Stone mass scales with heater size (~40 kg at 6 kW): 40 kg × 0.84 kJ/kg·K
 * × ~270 K ≈ 9 000 kJ, plus ~2 000 kJ for the steel body → ~1 800 kJ/kW.
 */
const HEATER_MASS_KJ_PER_KW = 1800;

/**
 * Panel/bench energy per m³ of room volume, in kJ. Approximates the wood
 * lining layer that has warmed through by the time the room reaches
 * temperature (the rest keeps absorbing during the session — covered by
 * the maintenance duty cycle).
 */
const LINING_MASS_KJ_PER_M3 = 550;

/** Heater efficiency for a well-insulated electric sauna */
const ELECTRIC_EFFICIENCY = 0.85;
/** Heater efficiency for log cabin / less insulated */
const LOG_CABIN_EFFICIENCY = 0.65;
/** Glass heat loss penalty: extra energy per m² of glass, in kJ */
const GLASS_LOSS_PER_SQM_KJ = 500;

/** Typical session length beyond heat-up, in minutes */
const SESSION_DURATION_MINUTES = 45;

/**
 * Default electricity price, in SEK/kWh, per market — every amount this
 * module returns is in SEK and gets converted at the render boundary, so
 * this must be a SEK figure regardless of market, never a native-currency
 * one. `RunningCostCalculatorPage.tsx` used to keep its OWN local default
 * (0.30, meant as EUR/kWh but fed straight into this SEK-typed parameter,
 * then never converted before display) while every other caller left this
 * parameter at its Swedish default — the running-cost estimate shown in the
 * builder and the one on the standalone calculator disagreed by ~2.3x for
 * the same electric sauna. Centralising it here is the fix: one convention,
 * every caller.
 *   se:   1.50 SEK/kWh (Swedish average incl. grid + tax)
 *   intl: ~0.30 EUR/kWh (a representative EU average) ≈ 3.45 SEK at the
 *         app's 11.5 SEK/EUR rate
 */
export const DEFAULT_ELECTRICITY_PRICE_SEK_BY_MARKET: Record<Market, number> = {
  se: 1.5,
  intl: 3.45,
};

/**
 * Firewood cost per session — roughly 15 kg of birch — expressed in SEK per
 * market, because every amount this module returns is in SEK and gets
 * converted at the render boundary. A flat Swedish bag price silently became
 * "€2" on intl, which is not what a bag of kiln-dried birch costs in the EU.
 *   se:   ~25 SEK per 15 kg bag
 *   intl: ~4 EUR per 15 kg bag ≈ 46 SEK at the app's 11.5 SEK/EUR rate
 */
export const WOOD_COST_PER_SESSION_SEK_BY_MARKET: Record<Market, number> = {
  se: 25,
  intl: 46,
};
/** Approximate kWh-equivalent per session for wood */
const WOOD_KWH_EQUIVALENT = 12;

/** Default sessions per month */
const DEFAULT_SESSIONS_PER_MONTH = 8;

// ─── Calculator ─────────────────────────────────────────────

export function calculateRunningCost(input: RunningCostInput): RunningCostResult {
  const {
    heaterKw,
    volumeM3,
    heaterType,
    glassAreaSqm = 0,
    isOutdoorLogCabin = false,
    sessionsPerMonth = DEFAULT_SESSIONS_PER_MONTH,
    market = 'se',
    woodCostPerSession,
  } = input;
  const electricityPriceSekPerKwh =
    input.electricityPriceSekPerKwh ??
    DEFAULT_ELECTRICITY_PRICE_SEK_BY_MARKET[market];

  if (heaterType === 'wood') {
    // Wood-fired: simpler model based on empirical fuel consumption
    // Heat-up time scales with volume, base ~30 min for 6 m³
    const baseHeatUpMin = 30;
    const volumeFactor = volumeM3 / 6;
    const heatUpMinutes = Math.round(baseHeatUpMin * Math.sqrt(volumeFactor));
    const woodCostSek = woodCostPerSession ?? WOOD_COST_PER_SESSION_SEK_BY_MARKET[market];

    return {
      heatUpMinutes,
      kwhPerSession: WOOD_KWH_EQUIVALENT,
      sessionCostSek: woodCostSek,
      costPerMonthSek: Math.round(woodCostSek * sessionsPerMonth),
    };
  }

  // ── Electric heater model ──────────────────────────────────

  const efficiency = isOutdoorLogCabin ? LOG_CABIN_EFFICIENCY : ELECTRIC_EFFICIENCY;

  // Energy to heat the air volume (kJ)
  const airEnergyKj = volumeM3 * AIR_DENSITY * AIR_SPECIFIC_HEAT * DELTA_T_C;

  // Total energy including thermal mass: stones/heater body scale with
  // heater power, the warmed lining layer scales with room volume.
  let totalEnergyKj =
    airEnergyKj + heaterKw * HEATER_MASS_KJ_PER_KW + volumeM3 * LINING_MASS_KJ_PER_M3;

  // Glass penalty
  totalEnergyKj += glassAreaSqm * GLASS_LOSS_PER_SQM_KJ;

  // Account for efficiency losses
  totalEnergyKj /= efficiency;

  // Convert to kWh for heat-up phase
  const heatUpKwh = totalEnergyKj / 3600;

  // Heat-up time in minutes
  const heatUpMinutes = Math.round((heatUpKwh / heaterKw) * 60);

  // Maintenance energy during session (heater cycles at ~40% duty)
  const maintenanceDuty = 0.4;
  const maintenanceKwh = heaterKw * maintenanceDuty * (SESSION_DURATION_MINUTES / 60);

  // Total session energy
  const kwhPerSession = Number((heatUpKwh + maintenanceKwh).toFixed(1));

  // Cost
  const sessionCostSek = Number((kwhPerSession * electricityPriceSekPerKwh).toFixed(1));
  const costPerMonthSek = Math.round(sessionCostSek * sessionsPerMonth);

  return {
    heatUpMinutes,
    kwhPerSession,
    sessionCostSek,
    costPerMonthSek,
  };
}
